import React, { type ForwardedRef, forwardRef, type ReactNode, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';
// import { AnimatePresence } from 'framer-motion';

import { NftCard } from '@pkg/components';
import { type UtcMilliseconds } from '@pkg/types';
import {
    FloorGameActivity,
    type FloorGameTierLabel,
    type NftCommonLibrary,
    type NftTimeline,
    type NftValueCommon,
    type YatOwnedNft,
} from '@pkg/types/floorGame';
import { formatUsdPrice, getCryptoCurrency } from '@pkg/utils/floorGame';

import { getCollectionEthUrl, getNftDetailsUrl } from '@app/helpers/url';

import { Activity, Credits, Hero, type HeroProps } from './sections';

import { StyledFloorGameCard } from './styles';

// import { FloorGameWebGLVariant } from '@pkg/webgl/types';

// import { LevelChangeTriangles } from './components';

// import { useWebGlState } from './hooks';
// import { ActionGroup } from './prototype';

// import { FloorGameCardVariant } from './FloorGameCard.motion';

// TODO: This API is still a work-in-progress.
// We do not yet know how we construct `notifications`,
// `ticks`, and "next" `thumbnails`. We may want to get
// rid of these `timeline` and `nextNfts` props.
export interface FloorGameCardProps extends YatOwnedNft {
    currentTier: FloorGameTierLabel;
    tokenId?: string;
    timeline?: NftTimeline;
    nextNfts?: NftCommonLibrary;
    listingsCount?: number;
    compact?: boolean;
    estimation?: boolean;
    // TODO: Temporary way to render <TicksMeter />.
    children?: ReactNode;
    lastSale?: UtcMilliseconds;
    notificationPortalRef?: HeroProps['notificationPortalRef'];
}

// TODO: Temporary until we can resolve the aggressive WebGL states.
const activity: FloorGameActivity = FloorGameActivity.Quiet;
// const isPrototypeMode = false;

function FloorGameCardComponent(
    {
        owner,
        id,
        name,
        contract,
        collection,
        asset,
        value,
        currentTier,
        tokenId,
        // status,
        market,
        // timeline = [],
        nextNfts = [],
        listingsCount = 0,
        compact = false,
        estimation = false,
        children,
        lastSale,
        notificationPortalRef,
    }: FloorGameCardProps,
    ref: ForwardedRef<HTMLDivElement>,
) {
    // Not extracted: lastUpdated
    const { price, usdPrice } = value;

    const currency = getCryptoCurrency(contract.blockchain);

    // TODO: How is `gains` determined? Is this value calculated
    // by the backend, or do we need to parse the "purchased at price" vs
    // "current floor price" to determine this percentage?
    const gains = 0;

    const localUsd = formatUsdPrice(usdPrice);
    // const collectionUrl = getCollectionEthUrl(collection?.slug);
    const commonPriceData: NftValueCommon = {
        currency,
        price,
        localConversion: localUsd,
    };

    // TODO: Is this calculated on the backend?
    // Is there something we need to do on the frontend to determine `activity`?
    // const activity: ActivityProps['activity'] = FloorGameActivity.Quiet;

    // TODO: What does this data really represent?
    // Do we get something from the backend that informs us what value(s) to use?
    // Or do we need to calculate this? Will we be informed when the `incoming`
    // value "resets" or do we have to run our own timer?
    // FloorGameIncomingEvent.Sale
    const incoming = undefined;

    const navigate = useNavigate();

    const handleAssetClick = useCallback(() => {
        if (contract && id) {
            const detailsUrl = getNftDetailsUrl({
                contractAddress: contract.address,
                tokenId: tokenId || '',
                goBackEnabled: true,
            });

            navigate(detailsUrl);
        }
    }, [id, contract, tokenId, navigate]);

    /*
    // TODO: Use `ForwardRef()` to the <GroupGridItem /> instead.
    // `ref` is needed on the <li /> element to be able to
    // follow when cards are shifting.
    const parentElement = typeof ref === 'function' || !ref?.current ? null : ref.current.parentElement;

    const { activity, hideCard, floorGameType, isPrototypeMode, playFloorGameUp, playFloorGameDown, playFire } =
        useWebGlState({
            node: parentElement,
            currentTier,
            price,
            name,
            collectionName: collection.name,
            image: asset.imageUrl
        });

    const prototypeControls = isPrototypeMode ? (
        <ActionGroup>
            <ActionGroup.Item content="Level up" onAction={playFloorGameUp} />
            <ActionGroup.Item content="Level down" onAction={playFloorGameDown} />
            <ActionGroup.Item content="Fire" onAction={playFire} />
        </ActionGroup>
    ) : null;

    const trianglesMarkup = hideCard ? (
        <LevelChangeTriangles directionDown={floorGameType === FloorGameWebGLVariant.TierDown} />
    ) : null;
    */

    return (
        // animate={hideCard ? FloorGameCardVariant.Hidden : FloorGameCardVariant.Show}
        <StyledFloorGameCard ref={ref}>
            <NftCard tier={currentTier} compact={compact} noBackground={activity === FloorGameActivity.OnFire}>
                <Hero
                    owner={owner}
                    asset={asset}
                    gains={gains}
                    compact={compact}
                    notificationPortalRef={notificationPortalRef}
                    onAction={handleAssetClick}
                />

                <Credits
                    nftName={name}
                    collection={collection}
                    url={getCollectionEthUrl(collection.slug)}
                    compact={compact}
                />

                <Activity
                    id={id}
                    value={commonPriceData}
                    tier={currentTier}
                    activity={activity}
                    incoming={incoming}
                    buyUrl={market?.buyUrl}
                    lastSale={lastSale}
                    nextNfts={nextNfts}
                    listingsCount={listingsCount}
                    estimation={estimation}
                    compact={compact}
                >
                    {children}
                </Activity>

                {/*
                    {prototypeControls}
                    <AnimatePresence>{trianglesMarkup}</AnimatePresence>
                */}
            </NftCard>
        </StyledFloorGameCard>
    );
}

// Passing generics to `forwardRef` to ensure `RefObject` only.
const FloorGameCardForwarded = forwardRef<HTMLDivElement, FloorGameCardProps>(FloorGameCardComponent);

export const FloorGameCard = Object.assign(FloorGameCardForwarded);
